import React, { useState, useEffect } from "react";
import "./Revenue.css";

const Revenue = () => {
	const [revenueData, setRevenueData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [sortConfig, setSortConfig] = useState({ key: "revenueMTD", direction: "desc" });
	const [expandedWorkspace, setExpandedWorkspace] = useState(null);
	const [trendRange, setTrendRange] = useState(6);

	useEffect(() => {
		fetchRevenueData();
	}, []);

	const fetchRevenueData = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/revenue`
			);
			if (!response.ok) {
				throw new Error("Failed to fetch revenue data");
			}
			const data = await response.json();
			setRevenueData(data);
			setLoading(false);
		} catch (err) {
			setError(err.message);
			setLoading(false);
		}
	};

	const formatCurrency = (amount) => {
		if (amount >= 10000000) {
			return `₹${(amount / 10000000).toFixed(2)} Cr`;
		}
		if (amount >= 100000) {
			return `₹${(amount / 100000).toFixed(2)} L`;
		}
		if (amount >= 1000) {
			return `₹${(amount / 1000).toFixed(1)}K`;
		}
		return `₹${Math.round(amount).toLocaleString("en-IN")}`;
	};

	const formatFullCurrency = (amount) => {
		return `₹${Math.round(amount).toLocaleString("en-IN")}`;
	};

	const formatGrowth = (value, showNew = false) => {
		if (value === undefined || value === null) return showNew ? "New" : "N/A";
		const formatted = Math.abs(value).toFixed(1);
		if (value > 0) return `+${formatted}%`;
		if (value < 0) return `-${formatted}%`;
		return "0%";
	};

	if (loading) {
		return (
			<div className="revenue-container">
				<div className="revenue-header">
					<h1>Revenue</h1>
				</div>
				<div className="revenue-loading">
					<div className="revenue-loading-spinner"></div>
					<p>Calculating revenue metrics...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="revenue-container">
				<div className="revenue-header">
					<h1>Revenue</h1>
				</div>
				<div className="revenue-error">
					<div className="revenue-error-icon">⚠️</div>
					<p>{error}</p>
					<button onClick={fetchRevenueData}>Retry</button>
				</div>
			</div>
		);
	}

	const growthIsPositive = revenueData.revenueGrowth > 0;
	const growthIsNegative = revenueData.revenueGrowth < 0;

	const getSortedWorkspaces = () => {
		if (!revenueData.workspaceDetails) return [];
		const sorted = [...revenueData.workspaceDetails];

		sorted.sort((a, b) => {
			if (sortConfig.key === "daysLeftInTrial" && sortConfig.direction === "asc") {
				// Trial ending soon: show trial workspaces first, sorted by days left ascending
				const aIsTrial = a.status === "Trial" ? 0 : 1;
				const bIsTrial = b.status === "Trial" ? 0 : 1;
				if (aIsTrial !== bIsTrial) return aIsTrial - bIsTrial;
				if (aIsTrial === 0 && bIsTrial === 0) {
					return a.daysLeftInTrial - b.daysLeftInTrial;
				}
				return b.revenueMTD - a.revenueMTD;
			}

			const aVal = a[sortConfig.key];
			const bVal = b[sortConfig.key];
			// Push null values (new workspaces) to the bottom
			if (aVal === null && bVal === null) return 0;
			if (aVal === null) return 1;
			if (bVal === null) return -1;
			return sortConfig.direction === "desc" ? bVal - aVal : aVal - bVal;
		});

		return sorted;
	};

	// Generate 12-month revenue outlook data
	const outlook12Month = (() => {
		if (!revenueData.monthlyTrends || revenueData.monthlyTrends.length < 2) return null;
		const trends = revenueData.monthlyTrends;
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const growthRates = [];
		for (let i = 1; i < trends.length; i++) {
			const prev = trends[i - 1].revenue;
			const curr = trends[i].revenue;
			if (prev > 0) growthRates.push((curr - prev) / prev);
		}
		const avgGrowth = growthRates.length > 0
			? growthRates.reduce((s, v) => s + v, 0) / growthRates.length
			: 0;

		const months = trends.map(t => ({
			label: t.month,
			revenue: t.revenue,
			isProjected: t.isProjected,
			isHistorical: !t.isProjected,
		}));

		const lastEntry = trends[trends.length - 1];
		const parts = lastEntry.month.split(' ');
		let mIdx = monthNames.indexOf(parts[0]);
		let yr = parseInt(parts[1]);
		const baseRevenue = lastEntry.revenue;
		let step = 1;
		while (months.length < 12) {
			mIdx++;
			if (mIdx >= 12) { mIdx = 0; yr++; }
			months.push({
				label: `${monthNames[mIdx]} ${yr}`,
				revenue: baseRevenue * Math.pow(1 + avgGrowth, step),
				isProjected: true,
				isHistorical: false,
			});
			step++;
		}

		let cumulative = 0;
		return months.map(m => {
			cumulative += m.revenue;
			return { ...m, arr: m.revenue * 12, cumulative };
		});
	})();

	// Recent revenue trend (last 6 months of actual + current projected month)
	const revenueTrendSeries = (() => {
		if (!revenueData.monthlyTrends || revenueData.monthlyTrends.length < 2) return null;
		return revenueData.monthlyTrends.slice(-6).map((t) => ({
			label: t.month,
			revenue: t.revenue,
			isProjected: t.isProjected,
			isHistorical: !t.isProjected,
		}));
	})();

	// Users deactivated per month (last 6 months). All points are historical.
	const deactivationSeries = (() => {
		if (!revenueData.monthlyDeactivations || revenueData.monthlyDeactivations.length < 2) return null;
		return revenueData.monthlyDeactivations.slice(-6).map((d) => ({
			label: d.month,
			count: d.count,
			isHistorical: true,
		}));
	})();

	const formatCount = (v) => Math.round(v).toLocaleString("en-IN");

	const lastMonthLabel = (() => {
		const d = new Date();
		d.setMonth(d.getMonth() - 1);
		return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
	})();

	const exportWorkspaceCSV = (ws) => {
		if (!ws.lastMonthBreakdown || ws.lastMonthBreakdown.length === 0) return;
		let csv = `${ws.name} - Revenue Breakdown - ${lastMonthLabel}\n\n`;
		csv += 'Day,Active Users,Revenue (INR)\n';
		let totalRevenue = 0;
		let totalUserDays = 0;
		ws.lastMonthBreakdown.forEach(d => {
			csv += `${d.day},${d.activeUsers},${d.revenue}\n`;
			totalRevenue += d.revenue;
			totalUserDays += d.activeUsers;
		});
		csv += `\nTotal,${totalUserDays},${Math.round(totalRevenue * 100) / 100}\n`;
		csv += `Average/Day,${Math.round(totalUserDays / ws.lastMonthBreakdown.length)},${Math.round((totalRevenue / ws.lastMonthBreakdown.length) * 100) / 100}\n`;
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${ws.name.replace(/\s+/g, '_')}_revenue_${lastMonthLabel.replace(/\s+/g, '_')}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<div className="revenue-container">
			{/* Header */}
			<div className="revenue-header">
				<h1>Revenue</h1>
				<p>
					Pricing: ₹{revenueData.meta.pricePerUserPerMonth}/user/month
					&nbsp;·&nbsp; Day {revenueData.meta.elapsedDays} of{" "}
					{revenueData.meta.daysInCurrentMonth}
				</p>
			</div>

			{/* Revenue Metric Cards */}
			<div className="revenue-metrics-grid">
				{/* Card 1: Current Month Revenue (MTD) */}
				<div className="revenue-metric-card mtd">
					<div className="revenue-metric-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
							<line x1="6" y1="4" x2="18" y2="4"></line>
							<line x1="6" y1="8" x2="18" y2="8"></line>
							<path d="M14.5 4C14.5 4 16 4 16 6C16 8 14.5 8 14.5 8H9L16 20"></path>
						</svg>
					</div>
					<div className="revenue-metric-value">
						{formatCurrency(revenueData.currentMonthRevenue)}
					</div>
					<div className="revenue-metric-label">
						Current Month Revenue (MTD)
					</div>
					<div className="revenue-metric-sub">
						{formatFullCurrency(revenueData.currentMonthRevenue)}
					</div>
				</div>

				{/* Card 2: Projected EOM Revenue */}
				<div className="revenue-metric-card projected">
					<div className="revenue-metric-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
						</svg>
					</div>
					<div className="revenue-metric-value">
						{formatCurrency(revenueData.projectedEOMRevenue)}
					</div>
					<div className="revenue-metric-label">
						Projected Monthly Revenue
					</div>
					<div className="revenue-metric-sub">
						{formatFullCurrency(revenueData.projectedEOMRevenue)}
					</div>
				</div>

				{/* Card 5: Revenue Growth vs Last Month */}
				<div
					className={`revenue-metric-card growth ${
						growthIsPositive
							? "positive"
							: growthIsNegative
							? "negative"
							: ""
					}`}
				>
					<div className="revenue-metric-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							{growthIsNegative ? (
								<>
									<polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
									<polyline points="17 18 23 18 23 12"></polyline>
								</>
							) : (
								<>
									<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
									<polyline points="17 6 23 6 23 12"></polyline>
								</>
							)}
						</svg>
					</div>
					<div className="revenue-metric-value">
						{formatGrowth(revenueData.revenueGrowth)}
					</div>
					<div className="revenue-metric-label">
						Revenue Growth vs Last Month
					</div>
					<div className="revenue-metric-sub">
						Last month: {formatFullCurrency(revenueData.lastMonthRevenue)}
					</div>
				</div>

				{/* Card 4: Workspaces in Trial */}
				<div className="revenue-metric-card trial">
					<div className="revenue-metric-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<circle cx="12" cy="12" r="10"></circle>
							<polyline points="12 6 12 12 16 14"></polyline>
						</svg>
					</div>
					<div className="revenue-metric-value">
						{revenueData.workspacesInTrial}
					</div>
					<div className="revenue-metric-label">
						Workspaces in Trial
					</div>
					<div className="revenue-metric-sub">
						of {revenueData.meta.totalWorkspaces} total · 15-day free trial
					</div>
				</div>

				{/* Card 5: Paid Workspaces */}
				<div className="revenue-metric-card paid">
					<div className="revenue-metric-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
							<line x1="1" y1="10" x2="23" y2="10"></line>
						</svg>
					</div>
					<div className="revenue-metric-value">
						{revenueData.paidWorkspaces}
					</div>
					<div className="revenue-metric-label">
						Paid Workspaces
					</div>
					<div className="revenue-metric-sub">
						of {revenueData.meta.totalWorkspaces} total
					</div>
				</div>
			</div>

			{/* 12-Month Revenue Outlook */}
			{outlook12Month && (
				<div className="revenue-outlook-section">
					<div className="revenue-outlook-header">
						<h2>Revenue Insights &amp; Projections</h2>
					</div>
					<div className="revenue-outlook-grid">
						<div className="revenue-chart-card">
							<div className="revenue-chart-title">
								<span className="rev-outlook-title-row">
									Annualized Run Rate (ARR)
									<span className="rev-info-btn rev-outlook-info-btn">
										i
										<span className="rev-info-tooltip rev-outlook-tooltip">
											<strong>How to read this chart</strong><br /><br />
											Each point shows what your yearly revenue would be if that month's revenue repeated for 12 straight months (Monthly Revenue × 12).<br /><br />
											<strong>Solid line</strong> = actual historical months<br />
											<strong>Dashed line</strong> = projected future months based on your average growth rate<br />
											<strong>Filled dots</strong> = recorded data &nbsp;·&nbsp; <strong>Hollow dots</strong> = projections<br /><br />
											Think of ARR as a speedometer — it shows your revenue pace at any given month. A rising line means you're accelerating growth.
										</span>
									</span>
								</span>
								<span className="revenue-outlook-subtitle">Current Month Revenue × 12</span>
							</div>
							<RevenueOutlookLineChart
								data={outlook12Month}
								dataKey="arr"
								color="#10b981"
								formatValue={formatCurrency}
								gradientId="arrGradient"
							/>
						</div>
						<div className="revenue-chart-card">
							<div className="revenue-chart-title">
								<span className="rev-outlook-title-row">
									12-Month Cumulative Revenue
									<span className="rev-info-btn rev-outlook-info-btn">
										i
										<span className="rev-info-tooltip rev-outlook-tooltip">
											<strong>How to read this chart</strong><br /><br />
											Each point shows the running total of all monthly revenues from the start of the 12-month window up to that month.<br /><br />
											<strong>Solid line</strong> = actual historical months<br />
											<strong>Dashed line</strong> = projected future months based on your average growth rate<br />
											<strong>Filled dots</strong> = recorded data &nbsp;·&nbsp; <strong>Hollow dots</strong> = projections<br /><br />
											This curve always rises — even slower months still add to the total. A steepening curve means revenue is accelerating. The final point shows your projected total revenue over the full 12-month period.
										</span>
									</span>
								</span>
								<span className="revenue-outlook-subtitle">Running total across projected months</span>
							</div>
							<RevenueOutlookLineChart
								data={outlook12Month}
								dataKey="cumulative"
								color="#3b82f6"
								formatValue={formatCurrency}
								gradientId="cumulativeGradient"
							/>
						</div>
						{revenueTrendSeries && (
							<div className="revenue-chart-card">
								<div className="revenue-chart-title">
									<span className="rev-outlook-title-row">
										Revenue Trend
										<span className="rev-info-btn rev-outlook-info-btn">
											i
											<span className="rev-info-tooltip rev-outlook-tooltip">
												<strong>How to read this chart</strong><br /><br />
												Each point shows that month's total revenue across all workspaces.<br /><br />
												<strong>Solid line</strong> = actual historical months<br />
												<strong>Dashed line</strong> = current month (projected to month-end)<br />
												<strong>Filled dots</strong> = recorded data &nbsp;·&nbsp; <strong>Hollow dots</strong> = projection<br /><br />
												Use this to see month-over-month revenue direction at a glance.
											</span>
										</span>
									</span>
									<span className="revenue-outlook-subtitle">Monthly revenue · last 6 months</span>
								</div>
								<RevenueOutlookLineChart
									data={revenueTrendSeries}
									dataKey="revenue"
									color="#06b6d4"
									formatValue={formatCurrency}
									gradientId="revenueTrendGradient"
								/>
							</div>
						)}
						{deactivationSeries && (
							<div className="revenue-chart-card">
								<div className="revenue-chart-title">
									<span className="rev-outlook-title-row">
										Users Deactivated
										<span className="rev-info-btn rev-outlook-info-btn">
											i
											<span className="rev-info-tooltip rev-outlook-tooltip">
												<strong>How to read this chart</strong><br /><br />
												Each point shows how many users were deactivated (marked inactive) that month.<br /><br />
												<strong>Note:</strong> this counts soft-deactivations only. Users from fully off-boarded companies are deleted from the database and cannot be counted here, so actual losses may be higher.
											</span>
										</span>
									</span>
									<span className="revenue-outlook-subtitle">Users deactivated per month · last 6 months</span>
								</div>
								<RevenueOutlookLineChart
									data={deactivationSeries}
									dataKey="count"
									color="#ef4444"
									formatValue={formatCount}
									gradientId="deactivationGradient"
								/>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Workspace Revenue Table */}
			{revenueData.workspaceDetails && revenueData.workspaceDetails.length > 0 && (
				<div className="revenue-table-section">
					<div className="revenue-table-header">
						<h2>Workspace Revenue</h2>
						<div className="revenue-sort-buttons">
							<button
								className={sortConfig.key === "revenueMTD" && sortConfig.direction === "desc" ? "active" : ""}
								onClick={() => setSortConfig({ key: "revenueMTD", direction: "desc" })}
							>
								Revenue
							</button>
							<button
								className={sortConfig.key === "revenueChange" && sortConfig.direction === "desc" ? "active" : ""}
								onClick={() => setSortConfig({ key: "revenueChange", direction: "desc" })}
							>
								Revenue Growth
							</button>
							<button
								className={sortConfig.key === "revenueChange" && sortConfig.direction === "asc" ? "active" : ""}
								onClick={() => setSortConfig({ key: "revenueChange", direction: "asc" })}
							>
								Revenue Decline
							</button>
							<button
								className={sortConfig.key === "daysLeftInTrial" && sortConfig.direction === "asc" ? "active" : ""}
								onClick={() => setSortConfig({ key: "daysLeftInTrial", direction: "asc" })}
							>
								Trial Ending Soon
							</button>
						</div>
					</div>

					<div className="revenue-table-wrapper">
						<table className="revenue-table">
							<thead>
								<tr>
									<th>#</th>
									<th>Workspace</th>
									<th>Status</th>
									<th>
										<span className="rev-th-with-info">
											User-Days (MTD)
											<span className="rev-info-btn">
												i
												<span className="rev-info-tooltip">
													Total number of billable user-days this month. For each day, we count activated users (Email Verified, Profile Done, or Complete) per workspace. E.g. 5 users active for 10 days = 50 user-days.
												</span>
											</span>
										</span>
									</th>
									<th>
										<span className="rev-th-with-info">
											Revenue (MTD)
											<span className="rev-info-btn">
												i
												<span className="rev-info-tooltip">
													Month-to-date revenue. Calculated as: for each day, (active users) × (₹389 ÷ days in month). Summed across all elapsed days this month.
												</span>
											</span>
										</span>
									</th>
									<th>
										<span className="rev-th-with-info">
											Projected Revenue
											<span className="rev-info-btn">
												i
												<span className="rev-info-tooltip">
													Projected end-of-month revenue for this workspace. Calculated as: (Revenue MTD ÷ elapsed days) × total days in month.
												</span>
											</span>
										</span>
									</th>
									<th>Gross Revenue</th>
									<th>Net Revenue</th>
									<th>vs Last Month</th>
									<th>Trial End Date</th>
									<th>All-Time Revenue</th>
								</tr>
							</thead>
							<tbody>
								{getSortedWorkspaces().map((ws, index) => {
									const changeIsPositive = ws.revenueChange !== null && ws.revenueChange > 0;
									const changeIsNegative = ws.revenueChange !== null && ws.revenueChange < 0;
									const changeIsNew = ws.revenueChange === null;
									const isExpanded = expandedWorkspace === ws.workspaceId;
									return (
										<React.Fragment key={ws.workspaceId}>
											<tr
												className={isExpanded ? "rev-row-expanded" : "rev-row-clickable"}
												onClick={() => {
													if (expandedWorkspace === ws.workspaceId) {
														setExpandedWorkspace(null);
													} else {
														setExpandedWorkspace(ws.workspaceId);
														setTrendRange(6);
													}
												}}
											>
												<td className="rev-rank">{index + 1}</td>
												<td className="rev-name">
													{ws.name}
													<span className={`rev-expand-icon ${isExpanded ? 'open' : ''}`}>
														<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
															<polyline points="6 9 12 15 18 9"></polyline>
														</svg>
													</span>
												</td>
												<td>
													<span className={`rev-status-badge ${ws.status === "Trial" ? "trial" : "active"}`}>
														{ws.status}
														{ws.status === "Trial" && ws.daysLeftInTrial <= 5 && (
															<span className="rev-trial-urgent"> · {ws.daysLeftInTrial}d left</span>
														)}
													</span>
												</td>
												<td className="rev-number">{ws.totalUserDays.toLocaleString("en-IN")}</td>
												<td className="rev-number rev-currency">{formatFullCurrency(ws.revenueMTD)}</td>
												<td className="rev-number rev-currency">{formatFullCurrency(ws.projectedRevenue)}</td>
												<td className="rev-number rev-currency">{formatFullCurrency(ws.grossRevenue)}</td>
												<td className="rev-number rev-currency">{formatFullCurrency(ws.netRevenue)}</td>
												<td className={`rev-number ${changeIsNew ? "rev-new" : changeIsPositive ? "rev-positive" : changeIsNegative ? "rev-negative" : ""}`}>
													{formatGrowth(ws.revenueChange, true)}
												</td>
												<td className="rev-date">
													{ws.trialEndDate
														? new Date(ws.trialEndDate).toLocaleDateString("en-US", {
															month: "short",
															day: "numeric",
															year: "numeric",
														})
														: "—"}
												</td>
												<td className="rev-number rev-currency">{formatFullCurrency(ws.allTimeRevenue)}</td>
											</tr>
											{isExpanded && (
												<tr className="rev-expanded-detail-row">
													<td colSpan="11">
														<div className="rev-detail-panel">
															<div className="rev-detail-header">
																<h3>{ws.name} — Detailed Revenue Analysis</h3>
																<button
																	className="rev-export-btn"
																	onClick={(e) => { e.stopPropagation(); exportWorkspaceCSV(ws); }}
																>
																	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
																		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
																		<polyline points="7 10 12 15 17 10"></polyline>
																		<line x1="12" y1="15" x2="12" y2="3"></line>
																	</svg>
																	Export CSV
																</button>
															</div>
															<div className="rev-detail-grid">
																<div className="rev-detail-breakdown">
																	<div className="rev-detail-breakdown-title">
																		{lastMonthLabel} — Daily Breakdown
																		<span className="rev-detail-breakdown-sub">
																			Total: {formatFullCurrency(ws.lastMonthRevenue)}
																		</span>
																	</div>
																	{ws.lastMonthBreakdown && ws.lastMonthBreakdown.length > 0 ? (
																		<div className="rev-detail-breakdown-scroll">
																			<table className="rev-detail-breakdown-table">
																				<thead>
																					<tr>
																						<th>Day</th>
																						<th>Active Users</th>
																						<th>Revenue</th>
																					</tr>
																				</thead>
																				<tbody>
																					{ws.lastMonthBreakdown.map((d) => (
																						<tr key={d.day}>
																							<td>{d.day}</td>
																							<td>{d.activeUsers}</td>
																							<td>{formatFullCurrency(d.revenue)}</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>
																	) : (
																		<div className="rev-detail-no-data">No breakdown data available</div>
																	)}
																</div>
																<div className="rev-detail-trend">
																	<div className="rev-detail-trend-header">
																		<span className="rev-detail-trend-title">Revenue Trend</span>
																		<div className="rev-detail-range-btns">
																			<button className={trendRange === 3 ? "active" : ""} onClick={(e) => { e.stopPropagation(); setTrendRange(3); }}>3M</button>
																			<button className={trendRange === 6 ? "active" : ""} onClick={(e) => { e.stopPropagation(); setTrendRange(6); }}>6M</button>
																			<button className={trendRange === 0 ? "active" : ""} onClick={(e) => { e.stopPropagation(); setTrendRange(0); }}>All</button>
																		</div>
																	</div>
																	{ws.monthlyHistory && ws.monthlyHistory.length > 0 ? (
																		<RevenueOutlookLineChart
																			data={(trendRange > 0 ? ws.monthlyHistory.slice(-trendRange) : ws.monthlyHistory).map(m => ({
																				...m,
																				label: m.month,
																				isHistorical: !m.isProjected,
																			}))}
																			dataKey="revenue"
																			color="#10b981"
																			formatValue={formatCurrency}
																			gradientId={`trend-${ws.workspaceId}`}
																			compact
																		/>
																	) : (
																		<div className="rev-detail-no-data">No trend data available</div>
																	)}
																</div>
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Monthly Trend Charts */}
			{revenueData.monthlyTrends && revenueData.monthlyTrends.length > 0 && (
				<div className="revenue-charts-section">
					<div className="revenue-charts-grid">
						{/* Monthly Revenue Trend */}
						<div className="revenue-chart-card">
							<div className="revenue-chart-title">Monthly Revenue Trend (Last 6 Months)</div>
							<RevenueBarChart
								data={revenueData.monthlyTrends.slice(-6)}
								dataKey="revenue"
								color="#10b981"
								formatValue={formatFullCurrency}
							/>
						</div>

						{/* Monthly User-Days Trend */}
						<div className="revenue-chart-card">
							<div className="revenue-chart-title">Monthly User-Days Trend (Last 6 Months)</div>
							<RevenueBarChart
								data={revenueData.monthlyTrends.slice(-6)}
								dataKey="userDays"
								color="#3b82f6"
								formatValue={(v) => v.toLocaleString("en-IN")}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const RevenueBarChart = ({ data, dataKey, color, formatValue }) => {
	if (!data || data.length === 0) return null;

	const maxValue = Math.max(...data.map((d) => d[dataKey]));
	const chartHeight = 200;
	const barGap = 12;

	return (
		<div className="rev-chart-container">
			<div className="rev-chart-y-axis">
				<span>{formatValue(maxValue)}</span>
				<span>{formatValue(Math.round(maxValue / 2))}</span>
				<span>0</span>
			</div>
			<div className="rev-chart-bars-area">
				<div className="rev-chart-grid-lines">
					<div className="rev-chart-grid-line" style={{ bottom: "100%" }}></div>
					<div className="rev-chart-grid-line" style={{ bottom: "50%" }}></div>
					<div className="rev-chart-grid-line" style={{ bottom: "0%" }}></div>
				</div>
				<div className="rev-chart-bars" style={{ height: chartHeight }}>
					{data.map((item, index) => {
						const heightPct = maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0;
						return (
							<div
								key={index}
								className="rev-chart-bar-group"
								style={{ gap: barGap }}
							>
								<div className="rev-chart-bar-wrapper" style={{ height: chartHeight }}>
									<div
										className={`rev-chart-bar ${item.isProjected ? "projected" : ""}`}
										style={{
											height: `${heightPct}%`,
											background: item.isProjected
												? `repeating-linear-gradient(135deg, ${color}40, ${color}40 3px, ${color}20 3px, ${color}20 6px)`
												: color,
											"--bar-color": color,
											border: item.isProjected ? `1.5px dashed ${color}` : "none",
											borderBottom: item.isProjected ? "none" : "none",
										}}
									>
										<span className="rev-chart-bar-value">
											{formatValue(item[dataKey])}
											{item.isProjected && (
												<span className="rev-chart-bar-projected-label"> (projected)</span>
											)}
										</span>
									</div>
								</div>
								<span className="rev-chart-bar-label">
									{item.month.split(" ")[0]}
									{item.isProjected && " ↗"}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

const RevenueOutlookLineChart = ({ data, dataKey, color, formatValue, gradientId, compact }) => {
	if (!data || data.length < 2) return null;

	const values = data.map(d => d[dataKey]);
	const maxVal = Math.max(...values);
	const rawMin = Math.min(...values);
	const minVal = rawMin > 0 ? rawMin * 0.85 : 0;
	const range = maxVal - minVal || 1;

	const W = compact ? 400 : 600, H = compact ? 80 : 210;
	const P = { t: compact ? 8 : 22, r: compact ? 6 : 12, b: compact ? 16 : 34, l: compact ? 36 : 58 };
	const cw = W - P.l - P.r;
	const ch = H - P.t - P.b;

	const px = (i) => P.l + (i / (data.length - 1)) * cw;
	const py = (v) => P.t + ch - ((v - minVal) / range) * ch;

	const splitIdx = data.findIndex(d => !d.isHistorical);
	const hasSplit = splitIdx > 0 && splitIdx < data.length;

	const toPath = (items, offset) =>
		items.map((d, i) => `${i === 0 ? 'M' : 'L'}${px(offset + i)},${py(d[dataKey])}`).join(' ');

	const fullPath = toPath(data, 0);
	const areaPath = `${fullPath} L${px(data.length - 1)},${P.t + ch} L${px(0)},${P.t + ch} Z`;
	const solidPath = hasSplit ? toPath(data.slice(0, splitIdx), 0) : fullPath;
	const dashedPath = hasSplit ? toPath(data.slice(splitIdx - 1), splitIdx - 1) : null;

	const yTicks = (compact ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map(p => minVal + range * p);

	return (
		<div className="rev-outlook-chart">
			<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
				<defs>
					<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={color} stopOpacity={compact ? 0.1 : 0.2} />
						<stop offset="100%" stopColor={color} stopOpacity="0.01" />
					</linearGradient>
				</defs>

				{yTicks.map((v, i) => (
					<g key={i}>
						<line
							x1={P.l} y1={py(v)} x2={P.l + cw} y2={py(v)}
							stroke={compact ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.06)"}
							strokeDasharray={i === 0 ? "none" : (compact ? "2,3" : "4,4")}
						/>
						<text x={P.l - 5} y={py(v) + (compact ? 2.5 : 3.5)} textAnchor="end" className={`rev-outlook-y-label ${compact ? 'compact' : ''}`}>
							{formatValue(v)}
						</text>
					</g>
				))}

				<path d={areaPath} fill={`url(#${gradientId})`} />

				<path
					d={solidPath} fill="none" stroke={color}
					strokeWidth={compact ? 1.25 : 2.5} strokeLinecap="round" strokeLinejoin="round"
				/>
				{dashedPath && (
					<path
						d={dashedPath} fill="none" stroke={color}
						strokeWidth={compact ? 1 : 2} strokeLinecap="round" strokeLinejoin="round"
						strokeDasharray={compact ? "3,3" : "6,4"} opacity={compact ? 0.45 : 0.55}
					/>
				)}

				{data.map((d, i) => (
					<g key={i} className="rev-outlook-point-group">
						<circle
							cx={px(i)} cy={py(d[dataKey])} r={compact ? 8 : 14}
							fill="transparent" className="rev-outlook-hit-area"
						/>
						<circle
							cx={px(i)} cy={py(d[dataKey])}
							r={compact ? (d.isHistorical ? 1.5 : 1) : (d.isHistorical ? 3.5 : 2.5)}
							className={`rev-outlook-dot ${!d.isHistorical ? 'rev-outlook-dot-projected' : ''}`}
							style={d.isHistorical ? { fill: color } : undefined}
							stroke={color}
							strokeWidth={d.isHistorical ? 0 : (compact ? 0.75 : 1.5)}
						/>
						<text
							x={px(i)} y={P.t + ch + (compact ? 10 : 18)}
							textAnchor="middle" className={`rev-outlook-x-label ${compact ? 'compact' : ''}`}
						>
							{d.label.split(' ')[0]}
						</text>
						<text
							x={px(i)} y={py(d[dataKey]) - (compact ? 5 : 10)}
							textAnchor="middle" className={`rev-outlook-hover-value ${compact ? 'compact' : ''}`}
						>
							{formatValue(d[dataKey])}
						</text>
					</g>
				))}
			</svg>
		</div>
	);
};

export default Revenue;
